import moment from "moment";
import React, { useContext, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import {
  Space,
  Button,
  Table,
  Popconfirm,
  Tag,
  Tooltip,
  DatePicker,
  Select,
  Popover,
  Dropdown,
  type MenuProps,
} from "antd";
import Search from "antd/lib/input/Search";
import {
  EyeOutlined,
  EditOutlined,
  UserOutlined,
  DeleteOutlined,
  UnlockOutlined,
  WalletOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  FileSearchOutlined,
  WarningOutlined,
  MoreOutlined,
  LockOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { debounce } from "ts-debounce";

import { MscdAccountFilter, SearchCtx } from "@/context/UserSearchProvider";
import { Role, Status, User, UserType } from "@/graphql";
import { useCanEdit } from "@/hooks/usePermission";

import { useUsers } from "./useUsers";
import { useExportSecuritiesCustomers } from "./useExportSecuritiesCustomers";
import { AddOrderWidget } from "../order/modal/create";

import { Option } from "./modal/create";
import { UnlockAccountWidget } from "./modal/recoverOtp";
import { EditUserPortfolioWidget } from "./modal/portfolio";

const { RangePicker } = DatePicker;

export function UserWidget() {
  const history = useHistory();
  const canEdit = useCanEdit("users-list");
  const exportCustomers = useExportSecuritiesCustomers();

  const [orderUser, setOrderUser] = useState<User | null>(null);
  const [showOrder, setShowOrder] = useState<boolean>(false);
  const [portfolioUser, setPortfolioUser] = useState<User | null>(null);
  const [showPortfolio, setShowPortfolio] = useState<boolean>(false);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const [recoverOtpUser, setRecoverOtpUser] = useState<User | null>(null);

  const {
    type,
    role,
    search,
    status,
    dateRange,
    mscdAccountFilter,
    setType,
    setRole,
    setSearch,
    setStatus,
    setDateRange,
    setMscdAccountFilter,
  } = useContext(SearchCtx);
  const {
    data,
    loading,
    page,
    limit,
    setPage,
    setLimit,
    getUsers,
    deleteUser,
    resetBalance,
    freezeUser,
    unfreezeUser,
  } = useUsers();

  const handleChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearch(value);
      setPage(0);
    },
    500,
  );

  return (
    <>
      <EditUserPortfolioWidget
        user={portfolioUser}
        show={showPortfolio}
        setShow={setShowPortfolio}
        key={portfolioUser?._id}
      />
      <AddOrderWidget
        user={orderUser}
        show={showOrder}
        setShow={setShowOrder}
        getOrders={getUsers}
      />
      <UnlockAccountWidget user={recoverOtpUser} setUser={setRecoverOtpUser} />
      <div className="page-header">
        <h3>Харилцагчдын жагсаалт</h3>
        <div className="page-header-buttons">
          <Popover
            placement="bottom"
            content={
              <div style={{ display: "flex" }}>
                <Space style={{ marginRight: 10 }}>
                  <RangePicker
                    allowClear
                    defaultValue={
                      dateRange
                        ? [
                            moment(dateRange.startDate),
                            moment(dateRange.endDate),
                          ]
                        : null
                    }
                    onChange={(values) => {
                      const [startDate, endDate] = values ?? [];
                      setDateRange(
                        startDate && endDate
                          ? {
                              startDate: startDate?.toDate().getTime(),
                              endDate: endDate.toDate().getTime(),
                            }
                          : null,
                      );
                      setPage(0);
                    }}
                    disabled={loading}
                  />
                </Space>
                <Space style={{ marginRight: 10, display: "block" }}>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Төрлөөр хайх"
                    onChange={(e) => {
                      setType(e);
                      setPage(0);
                    }}
                    defaultValue={type}
                  >
                    {Object.values(UserType).map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                </Space>
                <Space style={{ marginRight: 10, display: "block" }}>
                  <Select
                    showSearch
                    allowClear
                    style={{ width: 180 }}
                    placeholder="Эрхээр хайх"
                    defaultValue={role}
                    onChange={(e) => {
                      setRole(e);
                      setPage(0);
                    }}
                  >
                    {Object.values(Role).map((role) => (
                      <Option key={role} value={role}>
                        {role}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </div>
            }
            title="Дэлгэрэнгүй хайлт"
            trigger="click"
            open={showAdvancedFilter}
            onOpenChange={() => setShowAdvancedFilter(!showAdvancedFilter)}
          >
            <Button type="dashed" icon={<FileSearchOutlined />}>
              Дэлгэрэнгүй хайлт
            </Button>
          </Popover>

          <Space style={{ marginRight: 10, marginLeft: 10, display: "block" }}>
            <Select
              showSearch
              allowClear
              style={{ width: 200 }}
              placeholder="KYC статусаар хайх"
              defaultValue={status}
              onChange={(e) => {
                setStatus(e);
                setPage(0);
              }}
            >
              {Object.values(Status).map((type) => (
                <Option key={type} value={type}>
                  {type === Status.Confirmed
                    ? "БАТАЛГААЖСАН"
                    : type === Status.Notrequested
                      ? "ХҮСЭЛТ ИЛГЭЭГЭЭГҮЙ"
                      : type === Status.Pending
                        ? "ХҮЛЭЭГДЭЖ БАЙНА"
                        : "ТАТГАЛЗСАН "}
                </Option>
              ))}
            </Select>
          </Space>
          <Space style={{ marginRight: 10, display: "block" }}>
            <Select<MscdAccountFilter>
              allowClear
              style={{ width: 200 }}
              placeholder="ҮЦК данс"
              value={mscdAccountFilter}
              onChange={(e) => {
                setMscdAccountFilter(e);
                setPage(0);
              }}
            >
              <Option value="HAS_ACCOUNT">Данстай</Option>
              <Option value="SHOULD_CREATE_ACCOUNT">
                Данс үүсгэх шаардлагатай
              </Option>
              <Option value="HAS_ERROR">Алдаатай</Option>
            </Select>
          </Space>
          <Space style={{ marginRight: 10 }}>
            <Search
              allowClear
              defaultValue={search}
              placeholder="Хайх утгаа оруулна уу."
              onChange={handleChange}
            />
          </Space>
          {canEdit && (
            <Space>
              <Tooltip title="ҮЦТХТ-д данс нээгдсэн бүх харилцагчийг «Харилцагчийн мэдээлэл» загвараар, KYC асуумжийн хариултын хамт татна. Хуудасны шүүлтүүр үйлчлэхгүй.">
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exportCustomers.isLoading}
                  onClick={() => exportCustomers.mutate()}
                >
                  Excel татах
                </Button>
              </Tooltip>
              <Button
                type="primary"
                icon={<UserOutlined />}
                onClick={() => history.push("/users/add")}
              >
                Хэрэглэгч нэмэх
              </Button>
            </Space>
          )}
        </div>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            scroll={{ x: 900 }}
            dataSource={data?.users ?? []}
            size="small"
            loading={loading}
            rowClassName={(record) => (record.isFrozen ? "row-frozen" : "")}
            pagination={{
              current: page + 1,
              pageSize: limit,
              total: Number(data?.totalPages) * limit,
              showSizeChanger: true,
              onChange: (page, pageSize) => {
                setPage(page - 1);
                setLimit(pageSize);
              },
            }}
            bordered
            columns={[
              {
                title: "Гэрээ",
                key: "role",
                width: 60,
                render: (_, record) => (
                  <Tooltip
                    placement="topLeft"
                    title={record.contractDate ?? ""}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        className=""
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: record.contractDate
                            ? "green"
                            : "red",
                          borderRadius: "50%",
                        }}
                      />
                    </div>
                  </Tooltip>
                ),
              },
              {
                title: "Утасны дугаар",
                dataIndex: "phoneNo",
                key: "phoneNo",
                render: (text) => (
                  <span className="text_ellipse">{text ? `${text}` : "-"}</span>
                ),
              },
              {
                title: "Овог",
                dataIndex: "lastName",
                key: "lastName",
                render: (text) => (
                  <span className="text_ellipse">{text ?? "-"}</span>
                ),
              },
              {
                title: "Нэр",
                dataIndex: "firstName",
                key: "firstName",
                render: (text, record) => (
                  <span className="text_ellipse">
                    {text ?? "-"}
                    {record.isFrozen && (
                      <Tag
                        color="blue"
                        icon={<LockOutlined />}
                        style={{ marginLeft: 4 }}
                      >
                        Хөлдсөн
                      </Tag>
                    )}
                  </span>
                ),
              },
              {
                title: "Цахим хаяг",
                dataIndex: "email",
                key: "email",
                render: (text) => <span>{text ?? "-"}</span>,
              },
              {
                title: "KYC төлөв",
                dataIndex: "kycConfirmed",
                key: "kycConfirmed",
                render: (_, record) => {
                  if (record.kycInfo.status === Status.Confirmed) {
                    return <Tag color="green">БАТАЛГААЖСАН</Tag>;
                  }
                  if (record.kycInfo.status === Status.Notrequested) {
                    return <Tag>ХҮСЭЛТ ИЛГЭЭГЭЭГҮЙ</Tag>;
                  }
                  if (record.kycInfo.status === Status.Pending) {
                    return <Tag color="blue">ХҮЛЭЭГДЭЖ БАЙНА</Tag>;
                  }
                  if (record.kycInfo.status === Status.Rejected) {
                    return <Tag color="red">ТАТГАЛЗСАН</Tag>;
                  }
                },
              },
              {
                title: "ҮЦК",
                dataIndex: "mscdLastError",
                key: "mscdLastError",
                width: 60,
                render: (_, record) => {
                  if (!record.mscdLastError) return null;
                  const isRegistryConflict =
                    record.mscdLastError.includes("өөр") &&
                    record.mscdLastError.includes("BDCAccountId");
                  return (
                    <Tooltip title={record.mscdLastError}>
                      <Tag
                        color={isRegistryConflict ? "red" : "orange"}
                        icon={<WarningOutlined />}
                      >
                        {isRegistryConflict ? "РД давхардал" : "Алдаа"}
                      </Tag>
                    </Tooltip>
                  );
                },
              },

              {
                title: "",
                dataIndex: "button",
                key: "button",
                fixed: "right",
                width: 120,
                render: (_, record) => {
                  const moreMenuItems: MenuProps["items"] = [
                    {
                      key: "order",
                      icon: <PlusCircleOutlined />,
                      label: "Захиалга нэмэх",
                      onClick: () => {
                        // @ts-expect-error Removing unused fields from schema
                        setOrderUser(record);
                        setShowOrder(true);
                      },
                    },
                    {
                      key: "portfolio",
                      icon: <FileTextOutlined />,
                      label: "Багц өөрчлөх",
                      onClick: () => {
                        setPortfolioUser(record as User | null);
                        setShowPortfolio(true);
                      },
                    },
                    {
                      key: "otp",
                      icon: <UnlockOutlined />,
                      label: "OTP сэргээх",
                      // @ts-expect-error Removing unused fields from schema
                      onClick: () => setRecoverOtpUser(record),
                    },
                    { type: "divider" },
                    {
                      key: "freeze",
                      icon: <LockOutlined />,
                      label: record.isFrozen ? "Хөлдөөлт цуцлах" : "Хөлдөөх",
                      danger: !record.isFrozen,
                      onClick: () => {
                        if (record._id) {
                          if (record.isFrozen) {
                            unfreezeUser(record._id);
                          } else {
                            freezeUser(record._id);
                          }
                        }
                      },
                    },
                    {
                      key: "resetBalance",
                      icon: <WalletOutlined />,
                      label: "Данс устгах",
                      danger: true,
                      onClick: () => {
                        if (record._id) resetBalance(record._id);
                      },
                    },
                    {
                      key: "delete",
                      icon: <DeleteOutlined />,
                      label: "Хэрэглэгч устгах",
                      danger: true,
                      onClick: () => {
                        if (record._id) deleteUser(record._id);
                      },
                    },
                  ];

                  return (
                    <Space size="small">
                      <Tooltip title="Дэлгэрэнгүй">
                        <Link to={`/users/group-details/${record._id}`}>
                          <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            disabled={loading}
                          />
                        </Link>
                      </Tooltip>
                      {canEdit && (
                        <>
                          <Tooltip title="Засах">
                            <Link to={`/users/edit/${record._id}`}>
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                disabled={loading}
                              />
                            </Link>
                          </Tooltip>
                          <Dropdown
                            menu={{ items: moreMenuItems }}
                            trigger={["click"]}
                            disabled={loading}
                          >
                            <Button size="small" icon={<MoreOutlined />} />
                          </Dropdown>
                        </>
                      )}
                    </Space>
                  );
                },
              },
            ]}
          />
        </div>
      </div>
    </>
  );
}
